/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { useEffect, useState } from 'react'
import { Formik } from 'formik'
import * as Yup from 'yup'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import Modal from '../Modal'
import { ErrorMessageStyled } from '../shared/ErrorMessage'
import { useServiceContext } from '../../root'
import { useModal } from '../../contexts/ModalContext'
import { useEsignature } from '../../hooks/useEsignature'
import { DocTypeDropdown } from '../pickers/DocTypeDropdown'

/**
 * Change Document Type Modal Component
 *
 * Allows users to change the document type for selected files.
 * Supports batch operations for:
 * - Multiple files (standard)
 * - Property search results (batch)
 * - Folder-wide changes (batch)
 *
 * Features:
 * - Document type selection with folder type restrictions
 * - E-signature validation support
 * - Integration with file update events
 * - Cache invalidation for updated files
 *
 * Migrated from: lego/page/browser/ChangeDocumentTypeModal.jsx
 */

interface File {
  id: number
  parentFolderId?: number
  parentId?: number
  parent?: { id: number }
}

interface ChangeDocumentTypeModalProps {
  options: {
    file?: File
    parentFolderId?: number
    selectedIds: number[]
    selectedWholeResult?: boolean
    isPropertiesSearch?: boolean
    meta?: any
    filter?: any
    [key: string]: unknown
  }
  onClose?: () => void
}

interface DocTypeOption {
  value: number
  label: string
}

interface FormValues {
  doctype: DocTypeOption | null
}

const validationSchema = Yup.object({
  doctype: Yup.object().nullable().required('GENERAL_FORM_FIELD_IS_REQUIRED_ERROR')
})

export const ChangeDocumentTypeModal: React.FC<ChangeDocumentTypeModalProps> = ({ options, onClose }) => {
  const { t: translate } = useTranslation()
  const context = useServiceContext()
  const { toast, auth } = context.service
  const { api, $rootScope, constant } = context
  const { signAction } = useEsignature()
  const { hideModal } = useModal()
  const queryClient = useQueryClient()
  const [isOpen, setIsOpen] = useState(false)
  const [allowedTypes, setAllowedTypes] = useState<number[] | null>(null)
  const defaultFilterConst: FormValues = { doctype: null }

  useEffect(() => {
    setIsOpen(true)
    loadData()
  }, [])

  const doClose = () => {
    setIsOpen(false)
    if (onClose) {
      onClose()
    }
    hideModal()
  }

  /**
   * Get parent folder ID from various possible locations
   */
  const getParentFolderID = (): number => {
    return (
      options?.file?.parentFolderId ||
      options?.file?.parentId ||
      (options?.file?.parent?.id ? options.file.parent.id : options?.parentFolderId) ||
      0
    )
  }

  /**
   * Load folder type restrictions for document types
   */
  const loadData = async () => {
    try {
      const folderTypeData = await api.files.getFolderFolderType(getParentFolderID())

      // Check if folder has document type restrictions
      if (folderTypeData?.documentTypesAllowed !== constant.system.settings.folderType.documentTypesAllowed.ALL) {
        setAllowedTypes(folderTypeData?.allowedDocumentTypeTemplateIds || null)
      } else {
        setAllowedTypes(null) // No restrictions - all types allowed
      }
    } catch (error) {
      console.error('Error loading folder type data:', error)
      setAllowedTypes(null)
    }
  }

  /**
   * Save document type change
   * Handles e-signature validation if required
   */
  const save = async (values: FormValues) => {
    if (!values.doctype) {
      return
    }

    doClose()

    const doChangeDocTypeCallback = async (validation?: Record<string, unknown>) => {
      try {
        await api.files.changeFilesDocType(options.selectedIds, values.doctype!.value, validation, options)

        // Invalidate file queries to trigger refetch
        queryClient.invalidateQueries({ queryKey: ['files'] })
        queryClient.invalidateQueries({ queryKey: ['workflows'] })

        // Emit global events for compatibility (window event bus)
        window.dispatchEvent(new CustomEvent('updateFiles', { detail: true }))
        window.dispatchEvent(new Event('updateDashboard'))
        window.dispatchEvent(new Event('updateSearchFiles'))

        toast.success(translate('BROWSER_CHANGE_DOC_TYPE_SUCCESS_MSG'))
      } catch (error: any) {
        if (error?.data?.code === 417) {
          toast.error(translate('UNDERLYINF_DATA_WERE_CHNGD_TRY_AGAIN'))
        } else {
          toast.error(translate('ERROR_ERROR', { error: error?.data?.message || error?.message }))
        }
        throw error
      }
    }

    if (auth.requireESignatureForFileCommands()) {
      try {
        const validation = await signAction('ADMIN_ACTION', translate('ADMIN_ACTION_CHANGE_DOC_TYPE'))
        await doChangeDocTypeCallback(validation as any)
      } catch (error) {
        // E-signature cancelled or failed - error already handled by signAction
        console.log('E-signature cancelled or failed')
      }
    } else {
      await doChangeDocTypeCallback()
    }
  }

  return (
    <Formik
      validationSchema={validationSchema}
      enableReinitialize={true}
      initialValues={defaultFilterConst}
      onSubmit={save}
    >
      {({ values, setFieldValue, handleSubmit, errors, touched }) => (
        <Modal show={isOpen} handleClose={doClose} title={translate('BROWSER_CHANGE_DOC_TYPE_H_TITLE')}>
          <Modal.Body>
            <div className="form-group">
              <label className="control-label" htmlFor="doctype-dropdown">
                {translate('BROWSER_CHANGE_DOC_TYPE_LABEL')}
              </label>
              <DocTypeDropdown
                id="doctype-dropdown"
                name="doctype"
                placeholder="SELECT_DOC_TYPE_PLACEHOLDER"
                isSingle={true}
                required={true}
                allowedTypes={allowedTypes || undefined}
                value={values.doctype}
                onChange={(option: DocTypeOption) => {
                  setFieldValue('doctype', option)
                }}
              />
              <ErrorMessageStyled name="doctype" classesName="fs14" />
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="btn btn-link" onClick={doClose} data-action="cancel">
              {translate('CANCEL')}
            </button>
            <button type="button" className="btn btn-primary" onClick={() => handleSubmit()} data-action="ok">
              {translate('OK')}
            </button>
          </Modal.Footer>
        </Modal>
      )}
    </Formik>
  )
}

export default ChangeDocumentTypeModal
